import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { TokensAPI, AdminAPI, LeadsAPI, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import {
  KeyRound, Loader2, Shield, Copy, Check, Users, Package, CreditCard,
  BarChart3, Search, Trash2, X, ExternalLink, Save, RefreshCw, DollarSign,
} from "lucide-react";

/* ---------------- Root ---------------- */
export default function AdminPage() {
  const { toast } = useToast();
  const [secret, setSecret] = React.useState(localStorage.getItem("fdl_admin") || "");
  const [authed, setAuthed] = React.useState(false);
  const [tab, setTab] = React.useState("stats");

  const tryAuth = React.useCallback(async (sec) => {
    try {
      await AdminAPI.stats(sec);
      setAuthed(true);
      localStorage.setItem("fdl_admin", sec);
    } catch (err) {
      setAuthed(false);
      toast({ title: "Admin auth failed", description: apiErrorMessage(err, "Check secret"), variant: "destructive" });
    }
  }, [toast]);

  React.useEffect(() => { if (secret) tryAuth(secret); }, [secret, tryAuth]);

  const logout = () => {
    localStorage.removeItem("fdl_admin");
    setSecret("");
    setAuthed(false);
  };

  return (
    <div className="grain">
      <Navbar />
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <span className="chip"><Shield className="h-3 w-3 text-[#4aa4ff]" /> Back office</span>
            <h1 className="display-font mt-3 text-3xl font-bold">Furnace Design Lab · Admin</h1>
            <p className="mt-1 text-sm text-muted-foreground">Customers, subscriptions, tokens, and DTF service leads — one screen.</p>
          </div>
          {authed && (
            <Button variant="outline" onClick={logout} data-testid="admin-logout-btn">Lock</Button>
          )}
        </div>

        {!authed ? (
          <div className="mt-8 max-w-md rounded-2xl border border-border bg-card p-6">
            <Label>Admin secret</Label>
            <div className="relative mt-2">
              <KeyRound className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input value={secret} onChange={(e) => setSecret(e.target.value)} className="h-11 pl-9" placeholder="enter admin secret" type="password" data-testid="admin-secret-input" />
            </div>
            <Button className="btn-ember mt-4 h-11 w-full font-semibold" onClick={() => tryAuth(secret)} data-testid="admin-unlock-btn">Unlock</Button>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="mt-8">
            <TabsList className="bg-secondary/50 flex-wrap">
              <TabsTrigger value="stats" data-testid="admin-tab-stats"><BarChart3 className="mr-2 h-3.5 w-3.5" /> Overview</TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="mr-2 h-3.5 w-3.5" /> Customers</TabsTrigger>
              <TabsTrigger value="subs" data-testid="admin-tab-subs"><CreditCard className="mr-2 h-3.5 w-3.5" /> Subscriptions</TabsTrigger>
              <TabsTrigger value="leads" data-testid="admin-tab-leads"><Package className="mr-2 h-3.5 w-3.5" /> Service leads</TabsTrigger>
              <TabsTrigger value="tokens" data-testid="admin-tab-tokens"><KeyRound className="mr-2 h-3.5 w-3.5" /> Tokens</TabsTrigger>
            </TabsList>
            <TabsContent value="stats"><StatsTab secret={secret} /></TabsContent>
            <TabsContent value="users"><UsersTab secret={secret} /></TabsContent>
            <TabsContent value="subs"><SubscriptionsTab secret={secret} /></TabsContent>
            <TabsContent value="leads"><LeadsTab secret={secret} /></TabsContent>
            <TabsContent value="tokens"><TokensTab secret={secret} /></TabsContent>
          </Tabs>
        )}
      </section>
      <Footer />
    </div>
  );
}

/* ---------------- Overview ---------------- */
function StatsTab({ secret }) {
  const { toast } = useToast();
  const [s, setS] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await AdminAPI.stats(secret);
      setS(r.data);
    } catch (err) {
      toast({ title: "Couldn't load stats", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [secret, toast]);
  React.useEffect(() => { refresh(); }, [refresh]);

  if (loading || !s) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;

  return (
    <div className="mt-6 space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={refresh} className="gap-2"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Customers" value={s.users.total} sub={`${s.users.paid} paid · ${s.users.new_30d} new 30d`} />
        <StatCard title="Expiring soon" value={s.users.expiring_7d} sub="Pro / Creator token ≤ 7 days" />
        <StatCard title="Revenue (paid)" value={`$${s.revenue.usd.toFixed(2)}`} sub={`${s.revenue.paid_transactions} transactions`} tint="blue" />
        <StatCard title="Tokens" value={`${s.tokens.available} avail`} sub={`${s.tokens.redeemed}/${s.tokens.total} redeemed`} />
        <StatCard title="Service leads" value={s.leads.total} sub={`${s.leads.open} open · ${s.leads.won} won`} tint="gold" />
      </div>
    </div>
  );
}
function StatCard({ title, value, sub, tint }) {
  const t = tint === "blue" ? "text-[#4aa4ff]" : tint === "gold" ? "text-[#f5c845]" : "text-foreground";
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{title}</div>
      <div className={`display-font mt-1 text-3xl font-bold ${t}`}>{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}

/* ---------------- Customers ---------------- */
function UsersTab({ secret }) {
  const { toast } = useToast();
  const [q, setQ] = React.useState("");
  const [plan, setPlan] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [detail, setDetail] = React.useState(null);
  const [count, setCount] = React.useState(0);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (plan) params.plan = plan;
      const r = await AdminAPI.users(secret, params);
      setItems(r.data.users); setCount(r.data.count);
    } catch (err) {
      toast({ title: "Couldn't load users", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [secret, q, plan, toast]);

  React.useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const r = await AdminAPI.userGet(secret, id);
      setDetail(r.data);
    } catch (err) {
      toast({ title: "Couldn't open", description: apiErrorMessage(err), variant: "destructive" });
    }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="h-9 max-w-sm" data-testid="admin-users-search" />
        <Select value={plan || "all"} onValueChange={(v) => setPlan(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[180px]" data-testid="admin-users-plan-filter"><SelectValue placeholder="All plans" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="paid">Any paid</SelectItem>
            <SelectItem value="pro_token">Pro (Token)</SelectItem>
            <SelectItem value="creator_token">Creator (Token)</SelectItem>
            <SelectItem value="halftone_lifetime">Halftone Lifetime</SelectItem>
            <SelectItem value="pro_lifetime">All-Access Lifetime</SelectItem>
            <SelectItem value="expiring">Expiring ≤ 7d</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} className="gap-2"><RefreshCw className="h-3.5 w-3.5" /> Apply</Button>
        <div className="ml-auto mono text-[11px] text-muted-foreground">{count} users</div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border">
        <div className="grid grid-cols-[1.4fr_1fr_120px_130px_100px_80px] gap-2 border-b border-border bg-secondary/40 px-5 py-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          <div>Email</div><div>Plan</div><div>Expires</div><div>Signed up</div><div>Exports</div><div className="text-right">Open</div>
        </div>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No customers match.</div>
        ) : items.map((u) => (
          <div key={u.id} data-testid={`admin-user-row-${u.id}`} className="grid grid-cols-[1.4fr_1fr_120px_130px_100px_80px] items-center gap-2 border-b border-border/60 px-5 py-2 text-sm">
            <div className="truncate"><div className="truncate text-foreground">{u.email}</div><div className="truncate text-[11px] text-muted-foreground">{u.name}</div></div>
            <div><PlanBadge plan={u.plan} pro={u.pro_active} /></div>
            <div className="text-xs text-muted-foreground">{u.pro_expires_at ? new Date(u.pro_expires_at).toLocaleDateString() : "—"}</div>
            <div className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</div>
            <div className="text-xs text-muted-foreground">{u.exports_used_this_month ?? 0}</div>
            <div className="text-right"><Button size="sm" variant="ghost" onClick={() => openDetail(u.id)} data-testid={`admin-open-user-${u.id}`}><ExternalLink className="h-4 w-4" /></Button></div>
          </div>
        ))}
      </div>

      {detail && <UserDetailModal secret={secret} detail={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}

function PlanBadge({ plan, pro }) {
  const map = {
    free: { bg: "bg-secondary", text: "text-muted-foreground", label: "Free" },
    pro_token: { bg: "bg-[#4aa4ff]/15", text: "text-[#4aa4ff]", label: "Pro" },
    creator_token: { bg: "bg-[#f5c845]/15", text: "text-[#f5c845]", label: "Creator" },
    halftone_lifetime: { bg: "bg-[#4aa4ff]/15", text: "text-[#4aa4ff]", label: "Halftone LT" },
    pro_lifetime: { bg: "bg-[#a78bfa]/15", text: "text-[#a78bfa]", label: "All-Access LT" },
  };
  const c = map[plan] || map.free;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${c.bg} ${c.text}`}>
      {c.label}{pro && plan !== "free" ? " · active" : ""}
    </span>
  );
}

function UserDetailModal({ secret, detail, onClose, onChanged }) {
  const { toast } = useToast();
  const u = detail.user;
  const [grantDays, setGrantDays] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const patch = async (payload, label) => {
    setBusy(true);
    try {
      await AdminAPI.userPatch(secret, u.id, payload);
      toast({ title: label });
      onChanged?.(); onClose();
    } catch (err) {
      toast({ title: "Failed", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${u.email}? This removes their account and projects permanently.`)) return;
    setBusy(true);
    try {
      await AdminAPI.userDelete(secret, u.id);
      toast({ title: "User deleted" });
      onChanged?.(); onClose();
    } catch (err) {
      toast({ title: "Delete failed", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl chrome-border p-6" data-testid="admin-user-detail">
        <button className="absolute right-4 top-4 text-muted-foreground hover:text-foreground" onClick={onClose}><X className="h-5 w-5" /></button>
        <div>
          <span className="chip"><Users className="h-3 w-3 text-[#4aa4ff]" /> Customer</span>
          <h2 className="display-font mt-3 text-2xl font-bold">{u.email}</h2>
          <div className="mt-1 text-sm text-muted-foreground">{u.name} · joined {new Date(u.created_at).toLocaleDateString()}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <PlanBadge plan={u.plan} pro={u.pro_active} />
            {u.is_admin && <span className="rounded-full bg-[#f5c845]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-[#f5c845]">Admin</span>}
            {u.referral_code && <span className="mono rounded-full bg-secondary px-2 py-0.5 text-[10px]">Ref: {u.referral_code}</span>}
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          <MiniStat label="Exports (mo)" value={u.exports_used_this_month ?? 0} />
          <MiniStat label="Projects" value={detail.projects_count ?? 0} />
          <MiniStat label="Tokens redeemed" value={detail.tokens_redeemed ?? 0} />
          <MiniStat label="Pro expires" value={u.pro_expires_at ? new Date(u.pro_expires_at).toLocaleDateString() : "—"} />
        </div>

        <div className="mt-6 rounded-xl border border-border bg-card p-4">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Manual actions</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Grant Pro days</Label>
              <div className="mt-1 flex gap-2">
                <Input type="number" min={1} value={grantDays} onChange={(e) => setGrantDays(e.target.value)} className="h-10" placeholder="30" data-testid="admin-user-grant-days" />
                <Button disabled={busy || !grantDays} onClick={() => patch({ grant_days: parseInt(grantDays, 10) }, `Granted ${grantDays} days`)} data-testid="admin-user-grant-btn">Grant</Button>
              </div>
            </div>
            <div>
              <Label>Set plan directly</Label>
              <Select onValueChange={(v) => patch({ plan: v }, `Plan → ${v}`)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Choose plan…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro_token">Pro (Token)</SelectItem>
                  <SelectItem value="creator_token">Creator (Token)</SelectItem>
                  <SelectItem value="halftone_lifetime">Halftone Lifetime</SelectItem>
                  <SelectItem value="pro_lifetime">All-Access Lifetime</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 flex flex-wrap gap-2 pt-2">
              <Button variant="outline" disabled={busy} onClick={() => patch({ revoke: true }, "Revoked to Free")}>Revoke to Free</Button>
              <Button variant="outline" disabled={busy} onClick={() => patch({ is_admin: !u.is_admin }, `Admin: ${!u.is_admin}`)}>{u.is_admin ? "Remove admin" : "Make admin"}</Button>
              <Button variant="ghost" disabled={busy} className="ml-auto gap-2 text-red-400" onClick={remove}><Trash2 className="h-4 w-4" /> Delete</Button>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-border bg-card p-4">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Payment transactions ({detail.transactions.length})</div>
          {detail.transactions.length === 0 ? (
            <div className="mt-2 text-sm text-muted-foreground">No transactions.</div>
          ) : (
            <div className="mt-2 divide-y divide-border/60">
              {detail.transactions.map((t) => (
                <div key={t.session_id || t.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <div>
                    <div className="mono text-[11px] truncate text-muted-foreground">{t.session_id || t.id}</div>
                    <div className="text-xs">{t.lookup_key} · {t.created_at ? new Date(t.created_at).toLocaleString() : ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">${((t.amount || t.amount_total || 0) / 100).toFixed(2)}</div>
                    <div className="text-[11px] text-muted-foreground">{t.payment_status || t.status}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="display-font mt-1 text-xl font-bold">{value}</div>
    </div>
  );
}

/* ---------------- Subscriptions ---------------- */
function SubscriptionsTab({ secret }) {
  const { toast } = useToast();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [status, setStatus] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await AdminAPI.transactions(secret, status ? { status } : {});
      setItems(r.data.transactions);
    } catch (err) {
      toast({ title: "Couldn't load", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [secret, status, toast]);
  React.useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-6 space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3">
        <Label className="text-xs text-muted-foreground">Payment status</Label>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[180px]"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} className="gap-2"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border">
        <div className="grid grid-cols-[1.3fr_1fr_120px_130px_120px] gap-2 border-b border-border bg-secondary/40 px-5 py-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          <div>Customer</div><div>Plan</div><div>Amount</div><div>Created</div><div>Status</div>
        </div>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No transactions match.</div>
        ) : items.map((t) => (
          <div key={t.session_id || t.id} className="grid grid-cols-[1.3fr_1fr_120px_130px_120px] items-center gap-2 border-b border-border/60 px-5 py-2 text-sm">
            <div className="truncate">
              <div className="truncate text-foreground">{t.user_email || "—"}</div>
              <div className="mono truncate text-[10px] text-muted-foreground">{t.session_id || t.id}</div>
            </div>
            <div className="text-xs text-muted-foreground">{t.lookup_key || "—"}</div>
            <div className="text-sm font-semibold">${((t.amount || t.amount_total || 0) / 100).toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</div>
            <div className="text-xs">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                t.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-400" :
                t.payment_status === "failed" ? "bg-red-500/15 text-red-400" :
                "bg-secondary text-muted-foreground"
              }`}>{t.payment_status || t.status || "pending"}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Service leads ---------------- */
function LeadsTab({ secret }) {
  const { toast } = useToast();
  const [status, setStatus] = React.useState("");
  const [leads, setLeads] = React.useState([]);
  const [counts, setCounts] = React.useState({});
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await LeadsAPI.adminList(secret, status || undefined);
      setLeads(r.data.leads); setCounts(r.data.counts || {});
    } catch (err) {
      toast({ title: "Couldn't load leads", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [secret, status, toast]);
  React.useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const r = await LeadsAPI.adminGet(secret, id);
      setDetail(r.data);
    } catch (err) {
      toast({ title: "Couldn't open", description: apiErrorMessage(err), variant: "destructive" });
    }
  };

  const STATUS_TABS = [
    { id: "", label: "All" },
    { id: "new", label: `New (${counts.new ?? 0})` },
    { id: "contacted", label: `Contacted (${counts.contacted ?? 0})` },
    { id: "quoted", label: `Quoted (${counts.quoted ?? 0})` },
    { id: "won", label: `Won (${counts.won ?? 0})` },
    { id: "lost", label: `Lost (${counts.lost ?? 0})` },
  ];

  return (
    <div className="mt-6 space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3">
        {STATUS_TABS.map((t) => (
          <button key={t.id} data-testid={`admin-leads-filter-${t.id || "all"}`} onClick={() => setStatus(t.id)}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold ${status === t.id ? "bg-[#4aa4ff]/20 text-[#4aa4ff]" : "text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
        <Button variant="outline" size="sm" onClick={load} className="ml-auto gap-2"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border">
        <div className="grid grid-cols-[1.3fr_1fr_100px_100px_120px_80px] gap-2 border-b border-border bg-secondary/40 px-5 py-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          <div>Customer</div><div>Service</div><div>Qty</div><div>Status</div><div>Received</div><div className="text-right">Open</div>
        </div>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
        ) : leads.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No leads yet. Share the <a href="/services" className="text-[#4aa4ff]">/services</a> page to start collecting.</div>
        ) : leads.map((l) => (
          <div key={l.id} data-testid={`admin-lead-row-${l.id}`} className="grid grid-cols-[1.3fr_1fr_100px_100px_120px_80px] items-center gap-2 border-b border-border/60 px-5 py-2 text-sm">
            <div className="truncate"><div className="truncate">{l.name}</div><div className="truncate text-[11px] text-muted-foreground">{l.email}</div></div>
            <div className="truncate text-xs">{l.service}</div>
            <div className="text-xs">{l.quantity ?? "—"}</div>
            <div><LeadStatusBadge status={l.status} /></div>
            <div className="text-xs text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}</div>
            <div className="text-right"><Button size="sm" variant="ghost" onClick={() => openDetail(l.id)} data-testid={`admin-open-lead-${l.id}`}><ExternalLink className="h-4 w-4" /></Button></div>
          </div>
        ))}
      </div>

      {detail && <LeadDetailModal secret={secret} lead={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}

function LeadStatusBadge({ status }) {
  const map = {
    new: { c: "bg-[#4aa4ff]/15 text-[#4aa4ff]", l: "New" },
    contacted: { c: "bg-[#a78bfa]/15 text-[#a78bfa]", l: "Contacted" },
    quoted: { c: "bg-[#f5c845]/15 text-[#f5c845]", l: "Quoted" },
    won: { c: "bg-emerald-500/15 text-emerald-400", l: "Won" },
    lost: { c: "bg-red-500/15 text-red-400", l: "Lost" },
  };
  const s = map[status] || map.new;
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${s.c}`}>{s.l}</span>;
}

function LeadDetailModal({ secret, lead, onClose, onChanged }) {
  const { toast } = useToast();
  const [notes, setNotes] = React.useState(lead.admin_notes || "");
  const [status, setStatus] = React.useState(lead.status);
  const [quote, setQuote] = React.useState(lead.quoted_amount ?? "");
  const [busy, setBusy] = React.useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await LeadsAPI.adminUpdate(secret, lead.id, {
        status, admin_notes: notes,
        quoted_amount: quote === "" ? null : parseFloat(quote),
      });
      toast({ title: "Saved" });
      onChanged?.(); onClose();
    } catch (err) {
      toast({ title: "Save failed", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this lead?")) return;
    setBusy(true);
    try {
      await LeadsAPI.adminDelete(secret, lead.id);
      toast({ title: "Deleted" });
      onChanged?.(); onClose();
    } catch (err) {
      toast({ title: "Delete failed", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setBusy(false); }
  };

  const mailto = `mailto:${lead.email}?subject=${encodeURIComponent("Your Furnace Design Lab DTF quote")}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl chrome-border p-6" data-testid="admin-lead-detail">
        <button className="absolute right-4 top-4 text-muted-foreground hover:text-foreground" onClick={onClose}><X className="h-5 w-5" /></button>
        <span className="chip"><Package className="h-3 w-3 text-[#4aa4ff]" /> Service lead</span>
        <h2 className="display-font mt-3 text-2xl font-bold">{lead.name}</h2>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <a className="text-[#4aa4ff] hover:underline" href={`mailto:${lead.email}`}>{lead.email}</a>
          {lead.phone && <span>· {lead.phone}</span>}
          {lead.business && <span>· {lead.business}</span>}
          <LeadStatusBadge status={lead.status} />
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          <MiniStat label="Service" value={lead.service} />
          <MiniStat label="Quantity" value={lead.quantity ?? "—"} />
          <MiniStat label="Deadline" value={lead.deadline || "—"} />
          <MiniStat label="Budget" value={lead.budget || "—"} />
        </div>

        <div className="mt-6 rounded-xl border border-border bg-card p-4">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Message from customer</div>
          <p className="mt-2 whitespace-pre-wrap text-sm">{lead.message}</p>
        </div>

        {lead.attachments?.length > 0 && (
          <div className="mt-4 rounded-xl border border-border bg-card p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Attachments ({lead.attachments.length})</div>
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
              {lead.attachments.map((a, i) => (
                <div key={i} className="rounded-md border border-border bg-background p-1">
                  {a.data_base64 ? (
                    <img src={`data:${a.mime};base64,${a.data_base64}`} alt={a.filename} className="h-24 w-full rounded object-contain" />
                  ) : (
                    <div className="flex h-24 items-center justify-center text-xs text-muted-foreground">preview</div>
                  )}
                  <div className="mono truncate text-[9px] text-muted-foreground">{a.filename}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1" data-testid="admin-lead-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="quoted">Quoted</SelectItem>
                <SelectItem value="won">Won</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Quoted amount ($)</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input type="number" min={0} step="0.01" value={quote} onChange={(e) => setQuote(e.target.value)} className="h-10 pl-9" placeholder="e.g. 200" data-testid="admin-lead-quote-input" />
            </div>
          </div>
        </div>

        <div className="mt-3">
          <Label>Admin notes</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="Internal notes — customer never sees this." data-testid="admin-lead-notes" />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button className="btn-ember gap-2 font-semibold" disabled={busy} onClick={save} data-testid="admin-lead-save-btn"><Save className="h-4 w-4" /> Save changes</Button>
          <a href={mailto}><Button variant="outline" className="gap-2">Reply by email</Button></a>
          <Button variant="ghost" className="ml-auto gap-2 text-red-400" disabled={busy} onClick={remove}><Trash2 className="h-4 w-4" /> Delete</Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Tokens ---------------- */
function TokensTab({ secret }) {
  const { toast } = useToast();
  const [tier, setTier] = React.useState("pro");
  const [qty, setQty] = React.useState(5);
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [copied, setCopied] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const r = await TokensAPI.adminList(secret);
      setItems(r.data);
    } catch (err) {
      toast({ title: "Couldn't load tokens", description: apiErrorMessage(err), variant: "destructive" });
    }
  }, [secret, toast]);
  React.useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await TokensAPI.adminGenerate(qty, note, secret, tier);
      toast({ title: `Generated ${r.data.created} codes` });
      await load();
    } catch (err) {
      toast({ title: "Generate failed", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setBusy(false); }
  };
  const copy = (code) => { navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(""), 1500); };

  return (
    <div className="mt-6 space-y-6">
      <div className="grid gap-4 rounded-2xl border border-border bg-card p-6 sm:grid-cols-4">
        <div>
          <Label>Tier</Label>
          <div className="mt-1 flex gap-2">
            <button onClick={() => setTier("pro")} className={`flex-1 rounded-md border px-3 py-2 text-xs ${tier === "pro" ? "border-[#4aa4ff] bg-[#4aa4ff]/15 text-foreground" : "border-border text-muted-foreground"}`} data-testid="admin-token-tier-pro">Pro ($20)</button>
            <button onClick={() => setTier("creator")} className={`flex-1 rounded-md border px-3 py-2 text-xs ${tier === "creator" ? "border-[#f5c845] bg-[#f5c845]/15 text-foreground" : "border-border text-muted-foreground"}`} data-testid="admin-token-tier-creator">Creator ($45)</button>
          </div>
        </div>
        <div><Label>Quantity</Label><Input type="number" min={1} max={500} value={qty} onChange={(e) => setQty(parseInt(e.target.value || "1", 10))} className="mt-1 h-10" data-testid="admin-token-qty" /></div>
        <div><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-10" placeholder="batch: fb campaign" data-testid="admin-token-note" /></div>
        <div className="flex items-end"><Button onClick={generate} disabled={busy} className="btn-ember h-10 w-full font-semibold" data-testid="admin-token-generate-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate codes"}</Button></div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border">
        <div className="grid grid-cols-[1fr_100px_100px_150px_1fr_100px] gap-2 border-b border-border bg-secondary/40 px-6 py-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          <div>Code</div><div>Tier</div><div>Status</div><div>Redeemed</div><div>Note</div><div className="text-right">Copy</div>
        </div>
        {items.length === 0 && <div className="px-6 py-6 text-sm text-muted-foreground">No codes yet.</div>}
        {items.map((it) => (
          <div key={it.id} className="grid grid-cols-[1fr_100px_100px_150px_1fr_100px] items-center gap-2 border-b border-border/60 px-6 py-3 text-sm">
            <div className="mono truncate">{it.code}</div>
            <div><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${it.tier === "creator" ? "bg-[#f5c845]/15 text-[#f5c845]" : "bg-[#4aa4ff]/15 text-[#4aa4ff]"}`}>{it.tier || "pro"}</span></div>
            <div>{it.redeemed ? <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">Redeemed</span> : <span className="rounded-full bg-[#4aa4ff]/15 px-2 py-0.5 text-xs text-[#4aa4ff]">Available</span>}</div>
            <div className="truncate text-xs text-muted-foreground">{it.redeemed_at ? new Date(it.redeemed_at).toLocaleString() : "—"}</div>
            <div className="truncate text-xs text-muted-foreground">{it.note || "—"}</div>
            <div className="text-right"><Button variant="ghost" size="sm" onClick={() => copy(it.code)}>{copied === it.code ? <Check className="h-4 w-4 text-[#4aa4ff]" /> : <Copy className="h-4 w-4" />}</Button></div>
          </div>
        ))}
      </div>
    </div>
  );
}
